import { useMemo, useState } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { FocusRing } from '@change/ds-core-ui/util';
import { cn } from '@change/ds-utils';

import { useCurrentRouteConfig } from '#src/app/routes';

const SsrWarningBannerInner: React.FC<EmptyProps> = () => {
	const location = useLocation();

	const locationString = useMemo(() => `${location.pathname}${location.search}`, [location]);

	const [initialLocationString] = useState(locationString);
	const [dismissed, setDismissed] = useState(false);
	const [expanded, setExpanded] = useState(false);

	// we should not display the banner after navigating to another route
	const hasLocationChanged = initialLocationString !== locationString;

	const routeConfig = useCurrentRouteConfig();
	// checking if the current route has SSR enabled
	const ssrRoute = useMemo(() => routeConfig?.config.ssr && routeConfig.config.ssr.enabled, [routeConfig]);

	if (dismissed || hasLocationChanged || !ssrRoute) return null;

	return (
		<div
			className={cn(
				'text-typography-inverse-strong-fixed pointer-events-none fixed top-0 left-0 z-1000000 box-border w-screen text-base leading-6 transition-opacity',
				expanded ? 'opacity-95' : 'opacity-75',
			)}
		>
			<div className="bg-fill-brand-strong flex items-center justify-center px-8 py-1">
				<FocusRing focusRingClass="rounded-sm">
					<button
						type="button"
						onClick={() => setExpanded((v) => !v)}
						className="text-typography-inverse-strong-fixed pointer-events-auto cursor-pointer border-none bg-transparent text-base"
						aria-expanded={expanded}
					>
						⚠️ This SSR page is currently accessed in CSR mode ⚠️ {expanded ? '▲' : '▼'}
					</button>
				</FocusRing>
				<FocusRing focusRingClass="rounded-sm">
					<button
						type="button"
						onClick={() => setDismissed(true)}
						className="text-typography-inverse-strong-fixed pointer-events-auto absolute right-8 cursor-pointer border-none bg-transparent p-1"
						aria-label="Dismiss"
					>
						✕
					</button>
				</FocusRing>
			</div>
			{expanded && (
				<div className="bg-fill-brand-strong pointer-events-auto px-6 pb-3 text-sm leading-5">
					<p className="mb-1 font-semibold">Why does this matter?</p>
					<ul className="m-0 list-disc pl-5">
						<li>
							Some browser APIs are not available server-side (e.g. <code>localStorage</code>, <code>window</code>).
							Using them unconditionally can break the page when rendered with SSR.
						</li>
						<li>
							Some pages enable server-caching (sometimes only for guests), which prevents rendering sections that
							depend on user data on the server.
						</li>
						<li>
							Hydration mismatches between server and client output can cause visual glitches or errors in production.
						</li>
					</ul>
					<p className="mt-3 mb-1 font-semibold">What should you do?</p>
					<p className="m-0">
						Developing in CSR mode is perfectly fine, but make sure to test this page in SSR mode before pushing to git.
						This ensures that server-rendered output is correct and that no client-only code breaks the initial render.
					</p>
				</div>
			)}
		</div>
	);
};

export const SsrWarningBanner: React.FC<{ ssr: boolean }> = ({ ssr }) => {
	if (ssr || process.env.NODE_ENV !== 'development') return null;

	// using an inner component to avoid doing route filtering in non-dev env
	return <SsrWarningBannerInner />;
};
